import React, { useState, useEffect } from 'react';
import azureDevOpsService from './services/azureDevOpsService';
import { XCircle, AlertTriangle } from 'lucide-react';

export default function KpiDisabledTests() {
  const [selectedPipeline, setSelectedPipeline] = useState('');
  const [disabledTests, setDisabledTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('');

  const pipelines = [
    { id: '601', name: 'RF Admin API' },
    { id: '602', name: 'RF Admin Browser' },
    { id: '603', name: 'RF Config API' },
    { id: '604', name: 'RF Config Browser' },
    { id: '605', name: 'RF DE API' },
    { id: '606', name: 'RF DE Browser' },
    { id: '607', name: 'RF Pelias' },
    { id: '608', name: 'RF DP API' },
    { id: '609', name: 'RF DP Browser' },
    { id: '610', name: 'RF Investigation API' },
    { id: '611', name: 'RF Agentique API' },
    { id: '612', name: 'RF Agentique Browser' },
    { id: '613', name: 'RF Assistant API' },
    { id: '614', name: 'RF Assistant Browser' },
    { id: '615', name: 'RF OSINT API' },
    { id: '616', name: 'RF OSINT Browser' },
    { id: '303', name: 'ReadyAPI - Admin Tests' },
    { id: '304', name: 'ReadyAPI - Data Preparation' },
    { id: '305', name: 'ReadyAPI - Data Exploration' },
    { id: '355', name: 'ReadyAPI - Investigation' },
  ];

  const handlePipelineChange = (e) => {
    setSelectedPipeline(e.target.value);
  };

  useEffect(() => {
    if (!selectedPipeline) return;

    async function fetchDisabledTests() {
      setLoading(true);
      setDisabledTests([]);
      setProgress('Récupération des builds...');

      const normalizeName = (s) => (s || '').toString().trim().toLowerCase();

      // Récupère les noms de tests d'un build (runs puis fallback résultats)
      async function getBuildTestNames(buildNumericId) {
        const nameMap = new Map(); // key: normalized, value: display
        let runs = await azureDevOpsService.getTestRunsByBuildId(buildNumericId);
        if (runs && runs.length) {
          for (const run of runs) {
            const testCases = await azureDevOpsService.getTestCasesForRun(`run-${run.id}`);
            (testCases || []).forEach(t => {
              const raw = t?.name || t?.testCaseTitle || t?.automatedTestName;
              const key = normalizeName(raw);
              if (key && !nameMap.has(key)) nameMap.set(key, raw || key);
            });
          }
        }
        if (nameMap.size === 0) {
          const buildResults = await azureDevOpsService.getTestResultsForBuild(buildNumericId);
          (buildResults || []).forEach(r => {
            const raw = r?.testCaseTitle || r?.automatedTestName || r?.automatedTestStorage;
            const key = normalizeName(raw);
            if (key && !nameMap.has(key)) nameMap.set(key, raw || key);
          });
        }
        return nameMap;
      }

      try {
        // Récupérer des builds en remontant jusqu'à 12 mois en arrière
        const twelveMonthsAgo = new Date();
        twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
        const buildsData = await azureDevOpsService.getRecentBuildsByPipeline(selectedPipeline, 200, twelveMonthsAgo.toISOString());
        
        // Extraire le tableau de builds (la structure retournée est {pipeline-XXX: Array})
        let builds = [];
        if (buildsData && typeof buildsData === 'object') {
          const pipelineKey = `pipeline-${selectedPipeline}`;
          builds = buildsData[pipelineKey] || [];
        }
        
        if (!Array.isArray(builds) || builds.length < 2) {
          setProgress('Pas assez de builds pour analyser');
          setLoading(false);
          return;
        }

        setProgress(`Analyse de ${builds.length} builds...`);

        // Grouper les builds par mois
        const buildsByMonth = {};
        builds.forEach(build => {
          if (!build.date) return;
          const date = new Date(build.date);
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          
          if (!buildsByMonth[monthKey]) {
            buildsByMonth[monthKey] = [];
          }
          buildsByMonth[monthKey].push(build);
        });

        // Trier les mois du plus récent au plus ancien
        const sortedMonths = Object.keys(buildsByMonth).sort().reverse();
        
        if (sortedMonths.length < 2) {
          setProgress('Pas assez de mois différents pour analyser');
          setLoading(false);
          return;
        }

        // Prendre les 5 derniers mois (ou moins si pas disponibles)
        const lastMonths = sortedMonths.slice(0, Math.min(5, sortedMonths.length));
        
        setProgress(`Analyse de ${lastMonths.length} mois de builds...`);

        // Pour chaque mois, prendre le premier build (le plus ancien du mois)
        const firstBuildsOfMonths = [];
        lastMonths.forEach(month => {
          const buildsInMonth = buildsByMonth[month].sort((a, b) => 
            new Date(a.date) - new Date(b.date)
          );
          firstBuildsOfMonths.push(buildsInMonth[0]);
        });

        // Prendre le tout dernier build (le plus récent)
        const latestBuild = builds[0];

        setProgress('Récupération des tests du dernier build...');
        
        // Récupérer tous les tests du dernier build
        const buildIdStr = latestBuild.buildId || `build-${latestBuild.id}`;
        const numericBuildId = buildIdStr.replace('build-', '');
        const latestNameMap = await getBuildTestNames(numericBuildId);
        const latestTestNames = new Set(latestNameMap.keys());

        // Si on n'a rien pu récupérer pour le dernier build, ne pas déclarer tout en "désactivé"
        if (latestTestNames.size === 0) {
          setProgress("Impossible de récupérer les tests du dernier build; comparaison annulée");
          setLoading(false);
          return;
        }

        setProgress('Récupération des tests des premiers builds de chaque mois...');

        // Récupérer tous les tests des premiers builds de chaque mois
        const historicalTestNames = new Map(); // Map<normalizedName, {month, buildNumber, date, display}>
        for (let i = 0; i < firstBuildsOfMonths.length; i++) {
          const build = firstBuildsOfMonths[i];
          const buildDate = new Date(build.date);
          const monthName = buildDate.toLocaleDateString('fr-FR', { year: 'numeric', month: 'long' });
          
          setProgress(`Analyse build ${i + 1}/${firstBuildsOfMonths.length} (${monthName})...`);
          
          const buildIdStr = build.buildId || `build-${build.id}`;
          const numericBuildId = buildIdStr.replace('build-', '');
          
          const monthNameMap = await getBuildTestNames(numericBuildId);
          // Ne garder qu'une seule entrée par test (première occurrence historique)
          monthNameMap.forEach((display, key) => {
            if (!historicalTestNames.has(key)) {
              historicalTestNames.set(key, {
                month: monthName,
                buildNumber: numericBuildId,
                date: build.date,
                display
              });
            }
          });
        }

        setProgress('Comparaison des tests...');

        // Trouver les tests qui existaient historiquement mais pas dans le dernier build
        const disabled = [];
        historicalTestNames.forEach((info, normName) => {
          if (!latestTestNames.has(normName)) {
            disabled.push({
              name: info.display || normName,
              lastSeenInBuild: info.buildNumber,
              lastSeenDate: info.date,
              lastSeenMonth: info.month,
            });
          }
        });

        // Trier par nom
        disabled.sort((a, b) => a.name.localeCompare(b.name));

        setDisabledTests(disabled);
        setProgress('');
      } catch (error) {
        console.error('Erreur lors de l\'analyse des tests désactivés:', error);
        setProgress('Erreur lors de l\'analyse');
      } finally {
        setLoading(false);
      }
    }

    fetchDisabledTests();
  }, [selectedPipeline]);

  return (
    <div className="bg-white rounded shadow p-6">
      <h2 className="text-lg font-bold mb-4">Tests désactivés</h2>
      
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Sélectionner un pipeline
        </label>
        <select
          value={selectedPipeline}
          onChange={handlePipelineChange}
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">-- Choisir un pipeline --</option>
          {pipelines.map(p => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </div>

      {loading && (
        <div className="text-center py-4">
          <div className="text-gray-600">Chargement...</div>
          {progress && <div className="text-sm text-gray-500 mt-2">{progress}</div>}
        </div>
      )}

      {!loading && selectedPipeline && disabledTests.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <AlertTriangle className="mx-auto h-12 w-12 text-green-500 mb-2" />
          <p>Aucun test désactivé détecté</p>
        </div>
      )}

      {!loading && disabledTests.length > 0 && (
        <div>
          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded">
            <div className="flex items-start">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2 mt-0.5" />
              <div>
                <p className="font-semibold text-yellow-800">
                  {disabledTests.length} test(s) désactivé(s) détecté(s)
                </p>
                <p className="text-sm text-yellow-700 mt-1">
                  Ces tests étaient présents dans les premiers builds des 5 derniers mois mais n'apparaissent plus dans le dernier build
                </p>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nom du test
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dernier mois vu
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Build
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {disabledTests.map((test, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-start">
                        <XCircle className="h-4 w-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="font-medium">{test.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {test.lastSeenMonth || 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {test.lastSeenDate ? new Date(test.lastSeenDate).toLocaleDateString('fr-FR') : 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {test.lastSeenInBuild}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
